import * as React from 'react';
import { Component } from 'react';
import propz from 'propz';
import { SchoolEvent } from 'Src/models/event';
import { IndividualTournamentTeamPlayer } from 'Src/models/tournamentTeamPlayer';
import { SimpleModal } from 'Src/components/SimpleModal/SimpleModal';
import { getFullName } from 'Src/helpers/autocomplete/autocomplete';
import './SlotAssignmentModal.scss';

export interface SlotAssignment {
  student: IndividualTournamentTeamPlayer;
  replacingPlayerId?: string;
  replacedFirstName?: string;
  replacedLastName?: string;
}

interface Slot {
  id: string;
  participant?: any;
  assignedStudent?: IndividualTournamentTeamPlayer;
}

interface Props {
  isOpen: boolean;
  event: SchoolEvent;
  newStudents: IndividualTournamentTeamPlayer[];
  currentParticipants: any[];
  maxPlayers: number;
  activeSchoolId: string;
  onConfirm: (assignments: SlotAssignment[]) => void;
  onClose: () => void;
}

interface State {
  slots: Slot[];
  selectedStudentId: string | null;
}

export class SlotAssignmentModal extends Component<Props, State> {
  constructor(props) {
    super(props);
    this.state = {
      slots: this.buildSlots(props),
      selectedStudentId: null
    };
  }

  componentDidUpdate(prevProps) {
    if (prevProps.isOpen !== this.props.isOpen && this.props.isOpen) {
      this.setState({
        slots: this.buildSlots(this.props),
        selectedStudentId: null
      });
    }
  }

  buildSlots(props: Props): Slot[] {
    const { currentParticipants, maxPlayers } = props;
    const slots: Slot[] = [];
    const seen = new Set<string>();
    const uniqueParticipants = currentParticipants.filter(participant => {
      const key = String(participant.userId || participant.id);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });

    uniqueParticipants.forEach(participant => {
      slots.push({ id: participant.id || participant.userId, participant });
    });

    const emptyCount = Math.max(0, maxPlayers - uniqueParticipants.length);
    for (let index = 0; index < emptyCount; index++) {
      slots.push({ id: `free-${index}` });
    }

    return slots;
  }

  onStudentClick = (student: IndividualTournamentTeamPlayer) => {
    const { selectedStudentId, slots } = this.state;
    const assignedSlotIndex = slots.findIndex(
      slot => slot.assignedStudent && slot.assignedStudent.userId === student.userId
    );

    if (assignedSlotIndex !== -1) {
      const nextSlots = [...slots];
      nextSlots[assignedSlotIndex] = { ...nextSlots[assignedSlotIndex], assignedStudent: undefined };
      this.setState({ slots: nextSlots, selectedStudentId: null });
      return;
    }

    this.setState({ selectedStudentId: selectedStudentId === student.userId ? null : student.userId });
  };

  onSlotClick = (slotId: string) => {
    const { selectedStudentId, slots } = this.state;
    const { newStudents } = this.props;
    if (!selectedStudentId) return;

    const student = newStudents.find(item => item.userId === selectedStudentId);
    if (!student) return;

    const nextSlots = slots.map(slot => {
      if (slot.assignedStudent && slot.assignedStudent.userId === selectedStudentId) {
        return { ...slot, assignedStudent: undefined };
      }
      return slot.id === slotId ? { ...slot, assignedStudent: student } : slot;
    });

    this.setState({ slots: nextSlots, selectedStudentId: null });
  };

  onConfirm = () => {
    const assignments: SlotAssignment[] = this.state.slots
      .filter(slot => slot.assignedStudent)
      .map(slot => ({
        student: slot.assignedStudent as IndividualTournamentTeamPlayer,
        replacingPlayerId: slot.participant ? slot.participant.id || slot.participant._id : undefined,
        replacedFirstName: slot.participant && slot.participant.firstName ? slot.participant.firstName : '',
        replacedLastName: slot.participant && slot.participant.lastName ? slot.participant.lastName : ''
      }));

    this.props.onConfirm(assignments);
  };

  render() {
    const { isOpen, onClose, event, newStudents, activeSchoolId, maxPlayers } = this.props;
    const { slots, selectedStudentId } = this.state;
    if (!isOpen) return null;

    const eventName =
      propz.get(event, ['generatedNames', activeSchoolId], '') ||
      propz.get(event, ['generatedNames', 'official'], '') ||
      propz.get(event, ['name'], 'Tournament event');
    const assignedStudentIds = new Set(
      slots.filter(slot => slot.assignedStudent).map(slot => slot.assignedStudent!.userId)
    );
    const currentParticipantsCount = slots.filter(slot => slot.participant).length;
    const hasAnyAssignment = assignedStudentIds.size > 0;

    return (
      <SimpleModal
        isOpen={isOpen}
        showHeader={false}
        showFooter={false}
        customClass="slotAssignmentModalDialog"
        dialogStyle={{ maxWidth: 920 }}
        onBackdropClick={onClose}
      >
        <div className="slotAssignmentModal">
          <header className="slotAssignmentModal__head">
            <div className="slotAssignmentModal__headIcon" aria-hidden="true">
              <svg viewBox="0 0 24 24">
                <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <path d="M19 8v6m3-3h-6" />
              </svg>
            </div>
            <div>
              <h2>Assign participants to event</h2>
              <p>{eventName}</p>
            </div>
            <button type="button" onClick={onClose} aria-label="Close">
              &times;
            </button>
          </header>

          <div className="slotAssignmentModal__body">
            <div className="slotAssignmentModal__limitWarning" role="status">
              <span aria-hidden="true">!</span>
              <div>
                <strong>Adding these participants exceeds the event limit</strong>
                <p>
                  Your school can have up to {maxPlayers} participant{maxPlayers === 1 ? '' : 's'} in this event.{' '}
                  {currentParticipantsCount} {currentParticipantsCount === 1 ? 'place is' : 'places are'} already used
                  and you are trying to add {newStudents.length} more. Assign only the participants who should take the
                  available slots, or choose who they should replace.
                </p>
              </div>
            </div>

            <div className="slotAssignmentModal__picker">
              <section className="slotAssignmentModal__panel" aria-labelledby="new-participants-title">
                <div className="slotAssignmentModal__panelHead">
                  <div>
                    <h3 id="new-participants-title">New participants</h3>
                    <p>Choose one participant at a time.</p>
                  </div>
                  <span>{newStudents.length}</span>
                </div>
                <div className="slotAssignmentModal__list">
                  {newStudents.map(student => {
                    const isSelected = selectedStudentId === student.userId;
                    const isAssigned = assignedStudentIds.has(student.userId);
                    const formName = propz.get(student, ['form', 'name'], '');

                    return (
                      <button
                        type="button"
                        key={student.userId}
                        onClick={() => this.onStudentClick(student)}
                        className={`slotAssignmentModal__participant${isSelected ? ' isSelected' : ''}${
                          isAssigned ? ' isAssigned' : ''
                        }`}
                        aria-pressed={isSelected}
                      >
                        <span className="slotAssignmentModal__avatar" aria-hidden="true">
                          {getFullName(student).charAt(0)}
                        </span>
                        <span className="slotAssignmentModal__participantContent">
                          <strong>{getFullName(student)}</strong>
                          {formName && <small>{formName}</small>}
                          <em>
                            {isAssigned
                              ? 'Assigned — click to remove'
                              : isSelected
                              ? 'Now select an event slot'
                              : 'Not assigned — will not be added'}
                          </em>
                        </span>
                        <span className="slotAssignmentModal__participantState" aria-hidden="true">
                          {isAssigned ? '✓' : isSelected ? '→' : ''}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </section>

              <div className="slotAssignmentModal__transfer" aria-hidden="true">
                <span>→</span>
              </div>

              <section className="slotAssignmentModal__panel" aria-labelledby="event-slots-title">
                <div className="slotAssignmentModal__panelHead">
                  <div>
                    <h3 id="event-slots-title">Event slots</h3>
                    <p>
                      {selectedStudentId
                        ? 'Choose a slot for the selected participant.'
                        : 'Select a participant first.'}
                    </p>
                  </div>
                  <span>{slots.length}</span>
                </div>
                <div className="slotAssignmentModal__list">
                  {slots.map((slot, index) => {
                    const isEmpty = !slot.participant;
                    const hasStudent = !!slot.assignedStudent;
                    const isActive = !!selectedStudentId;

                    return (
                      <button
                        type="button"
                        key={slot.id}
                        onClick={() => this.onSlotClick(slot.id)}
                        disabled={!isActive}
                        className={`slotAssignmentModal__slot${isEmpty ? ' isEmpty' : ''}${
                          hasStudent ? ' isAssigned' : ''
                        }${isActive ? ' isActive' : ''}`}
                      >
                        <span className="slotAssignmentModal__slotNumber">{index + 1}</span>
                        <span className="slotAssignmentModal__slotContent">
                          {slot.assignedStudent ? (
                            <>
                              <small>New participant</small>
                              <strong>{getFullName(slot.assignedStudent)}</strong>
                            </>
                          ) : slot.participant ? (
                            <>
                              <small>Currently allocated</small>
                              <strong>
                                {slot.participant.lastName} {slot.participant.firstName}
                              </strong>
                            </>
                          ) : (
                            <>
                              <small>Available</small>
                              <strong>{isActive ? 'Assign to this slot' : 'Empty slot'}</strong>
                            </>
                          )}
                          {slot.assignedStudent && slot.participant && (
                            <em>
                              Replaces {slot.participant.lastName} {slot.participant.firstName}
                            </em>
                          )}
                          {slot.assignedStudent && !slot.participant && <em>Uses an available slot</em>}
                        </span>
                        <span className="slotAssignmentModal__slotState" aria-hidden="true">
                          {hasStudent ? '✓' : isActive ? '+' : ''}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </section>
            </div>
          </div>

          <footer className="slotAssignmentModal__foot">
            <div>
              <strong>
                {assignedStudentIds.size} of {newStudents.length}
              </strong>
              <span>participants will be added</span>
            </div>
            <div className="slotAssignmentModal__actions">
              <button type="button" onClick={onClose}>
                Cancel
              </button>
              <button type="button" className="isPrimary" onClick={this.onConfirm} disabled={!hasAnyAssignment}>
                Confirm allocation
              </button>
            </div>
          </footer>
        </div>
      </SimpleModal>
    );
  }
}
